import React from 'react';
import PropTypes from 'prop-types';
import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useI18n } from '../../../../../context/I18nContext';
import { useModal } from '../../../../../context/ModalContext';
import { useRoomContext } from '../../../../../context/RoomContext';
import { useMatrixClient } from '../../../../../context/MatrixContext';
import FancyTimeStamp from '../../../../FancyTimeStamp';
import MessageStatusIndicator from './MessageStatusIndicator';
import SmartEmojiPicker from './SmartEmojiPicker';
import MessageReactions from './MessageReactions';
import { useMediaThumbnail } from '../../../../../hooks/useMediaThumbnail';
import { formatFileSize, getFileType } from '../../../../../utils/fileUtils';
import logger from '../../../../../utils/logger';
import { getAttachmentIcon } from '../../../../../utils/fileUtils';
import { getRedactionInfo, getRedactionTranslation } from '../../../../../utils/redactionUtils';
import AttachmentPreviewModal from './AttachmentPreviewModal';
import FileAttachmentModal from './FileAttachmentModal';
import './MessageBubble.css';

// EventShieldReason enum replacement
const EventShieldReason = {
  UNVERIFIED_IDENTITY: 'UNVERIFIED_IDENTITY',
  UNSIGNED_DEVICE: 'UNSIGNED_DEVICE',
  UNKNOWN_DEVICE: 'UNKNOWN_DEVICE',
  VERIFICATION_VIOLATION: 'VERIFICATION_VIOLATION',
};

const KebabMenuIcon = ({ onClick = () => {}, renderSide = 'left' }) => (
  <svg
    width='14'
    height='14'
    viewBox='0 0 14 14'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    onClick={onClick}
    style={{
      transform: renderSide === 'right' ? 'scaleX(-1)' : 'none',
      paddingLeft: '10px',
    }}>
    <path
      d='M2 3.5C2.9625 3.5 3.75 2.7125 3.75 1.75C3.75 0.7875 2.9625 0 2 0C1.0375 0 0.25 0.7875 0.25 1.75C0.25 2.7125 1.0375 3.5 2 3.5ZM2 5.25C1.0375 5.25 0.25 6.0375 0.25 7C0.25 7.9625 1.0375 8.75 2 8.75C2.9625 8.75 3.75 7.9625 3.75 7C3.75 6.0375 2.9625 5.25 2 5.25ZM2 10.5C1.0375 10.5 0.25 11.2875 0.25 12.25C0.25 13.2125 1.0375 14 2 14C2.9625 14 3.75 13.2125 3.75 12.25C3.75 11.2875 2.9625 10.5 2 10.5Z'
      fill='#4A4A4A'
    />
  </svg>
);

KebabMenuIcon.propTypes = {
  onClick: PropTypes.func,
  renderSide: PropTypes.oneOf(['left', 'right']),
};

const UnverifiedDeviceIcon = () => (
  <span
    title='Message sent from an unverified device'
    style={{ color: '#e67e22', marginLeft: 6, verticalAlign: 'middle' }}>
    <i className='fa fa-exclamation-triangle' />
  </span>
);

// Context menu dimensions (must match CSS width and estimated heights)
const CONTEXT_MENU_WIDTH = 166;
// Each menu item is ~36px (10px padding top + 16px content + 10px padding bottom)
// User messages: 3-5 items (Reply, Edit?, Details, Edit History?, Delete?) = 108-180px
const CONTEXT_MENU_HEIGHT_USER_MIN = 108; // Without Edit or Edit History (3 items)
const CONTEXT_MENU_HEIGHT_USER_MAX = 180; // With Edit and Edit History (5 items)
// Other messages: 2-4 items (Reply, Details, Edit History?, Delete?) = 72-144px
const CONTEXT_MENU_HEIGHT_OTHER_MIN = 72; // Without Edit History or Delete (2 items)
const CONTEXT_MENU_HEIGHT_OTHER_MAX = 144; // With Edit History and Delete (4 items)

const MessageBubble = ({
  message,
  name = '',
  isUser = false,
  shield = null,
  isRead = false,
  isFirstInGroup = false,
  isLastInGroup = false,
  onDeleteMessage = (_messageId) => {},
  onReplyMessage = (_message, _senderName, _content) => {},
  onEditMessage = (_message, _content) => {},
  isEditHighlighted = false,
  isSelected = false,
  isReply = false,
  isEdited = false,
  originalReplyMessage = null,
  onReactionDetailsOpen = () => {},
  onMessageDetailsOpen = () => {},
  onFocusMessage = () => {},
  onEditHistoryOpen = () => {},
}) => {
  const { t } = useI18n();
  const { showConfirm } = useModal();
  const { toggleReaction, canDeleteMessage, members } = useRoomContext();
  const client = useMatrixClient();

  const [isHovered, setIsHovered] = useState(false);
  const [isHoveringReactions, setIsHoveringReactions] = useState(false);
  const [hasRevealedDetails, setHasRevealedDetails] = useState(isLastInGroup);
  const [isContextMenuOpen, setOpenContextMenu] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isMenuSticky, setIsMenuSticky] = useState(false);
  const [isAttachmentModalOpen, setIsAttachmentModalOpen] = useState(false);
  const [isFileModalOpen, setIsFileModalOpen] = useState(false);
  const [imageLoadError, setImageLoadError] = useState(false);
  const [optimisticReaction, setOptimisticReaction] = useState(null); // { emoji, timestamp }
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [isOpeningDetails, setIsOpeningDetails] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const contextMenuRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const bubbleRef = useRef(null);
  const portalContainerRef = useRef(null);

  // Sync hasRevealedDetails with isLastInGroup prop changes
  // This ensures that when message groups are recalculated (e.g., after decryption),
  // the timestamp visibility is correctly reset based on the updated position
  useEffect(() => {
    setHasRevealedDetails(isLastInGroup);
  }, [isLastInGroup]);

  // Check delete permissions when message changes
  // This allows moderators to delete any message, not just their own
  useEffect(() => {
    const checkDeletePermission = async () => {
      if (!message || !canDeleteMessage) {
        setCanDelete(false);
        return;
      }

      try {
        const hasPermission = await canDeleteMessage(message.getId());
        setCanDelete(hasPermission);
      } catch (error) {
        logger.error('Failed to check delete permission:', error);
        setCanDelete(false);
      }
    };

    checkDeletePermission();
  }, [message, canDeleteMessage]);

  // Merge server reactions with optimistic updates
  const displayReactions = (() => {
    if (!optimisticReaction) {
      return message._reactions;
    }

    // Create a new Map with optimistic changes applied
    const merged = new Map();
    const currentUserId = client?.getUserId();

    // Copy all server reactions, excluding current user's reactions
    message._reactions.forEach((reactors, emoji) => {
      const filtered = reactors.filter((r) => r.senderId !== currentUserId);
      if (filtered.length > 0) {
        merged.set(emoji, filtered);
      }
    });

    // Add the optimistic reaction
    if (merged.has(optimisticReaction.emoji)) {
      merged.get(optimisticReaction.emoji).push({
        senderId: currentUserId,
        eventId: 'optimistic',
        displayName: 'You',
        isCurrentUser: true,
      });
    } else {
      merged.set(optimisticReaction.emoji, [
        {
          senderId: currentUserId,
          eventId: 'optimistic',
          displayName: 'You',
          isCurrentUser: true,
        },
      ]);
    }

    return merged;
  })();

  const showUnverifiedIcon =
    !isUser &&
    shield &&
    (shield.reason === EventShieldReason.UNVERIFIED_IDENTITY ||
      shield.reason === EventShieldReason.UNSIGNED_DEVICE ||
      shield.reason === EventShieldReason.UNKNOWN_DEVICE ||
      shield.reason === EventShieldReason.VERIFICATION_VIOLATION);

  const isUserClass = isUser ? 'user' : '';
  const positionClass = isFirstInGroup ? 'first' : 'continuation';

  const isRedacted = message.isRedacted();

  // Get content from either clear content (decrypted) or regular content
  // NOTE: Not using useMemo here for encrypted messages because we need to check
  // the message state on every render to detect when decryption completes
  const getProcessedContent = () => {
    if (isRedacted) {
      const redactionInfo = getRedactionInfo(message, members, client);
      const translation = getRedactionTranslation(redactionInfo, isUser);
      // Only pass params if they're non-empty (for test compatibility)
      return Object.keys(translation.params).length > 0
        ? t(translation.key, translation.params)
        : t(translation.key);
    }

    // For edited messages, use the stored edited content if available
    let content;
    let hasClearContent = false;
    if (message._isEdited && message._editedContent) {
      content = message._editedContent;
      hasClearContent = true; // Edited content means message was successfully decrypted
    } else {
      // Get content from either clear content (decrypted) or regular content
      hasClearContent = message.getClearContent && message.getClearContent();
      content = hasClearContent ? message.getClearContent() : message.getContent();

      logger.debug(`MessageBubble ${message.getId()}: type=${message.getType()}, hasClearContent=${Boolean(hasClearContent)}`);

      // Fallback: check for m.new_content in the current content (for public rooms)
      if (message._isEdited && content['m.new_content']) {
        content = content['m.new_content'];
      }
    }

    // Check if it's an encrypted message that failed to decrypt
    if (message.getType() === 'm.room.encrypted' && !hasClearContent) {
      // Check if there's a decryption error
      const decryptionError = message.decryptionError;
      if (decryptionError) {
        // More user-friendly messages for common errors
        if (decryptionError.message?.includes('before this device logged in')) {
          return '🔒 Unable to decrypt - Message was sent before you logged in on this device';
        }
        if (decryptionError.message?.includes('key backup')) {
          return '🔒 Unable to decrypt - Recovery key needed';
        }
        return `🔒 Unable to decrypt: ${decryptionError.message || 'Unknown error'}`;
      }
      logger.debug(`MessageBubble ${message.getId()}: Still waiting for keys`);
      return '🔒 Encrypted message (waiting for keys)';
    }
    return content;
  };

  const processedContent = getProcessedContent();

  // --- Start: Variables now in main component scope ---
  const msgtype = processedContent?.msgtype;

  const isAttachment = ['m.image', 'm.video', 'm.audio', 'm.file'].includes(msgtype);
  const shouldShowDetails = isAttachment || isLastInGroup || hasRevealedDetails;

  const filename = isAttachment
    ? processedContent.filename || processedContent.body || 'Attachment'
    : null;

  // NEW: Add fileSize and mimeType to main scope
  const fileSize = isAttachment ? processedContent.info?.size : null;
  const mimeType = isAttachment ? processedContent.info?.mimetype : null;
  const formattedSize = formatFileSize(fileSize);

  // --- End: Variables now in main component scope ---
  const { thumbnailUrl: previewSrc } = useMediaThumbnail(message, processedContent.info?.thumbnail_url);

  // This function now just focuses on rendering the body of the message bubble
  const getMessageBody = () => {
    if (typeof processedContent === 'string') {
      // This handles redacted messages and decryption errors
      return processedContent;
    }

    // This function now uses the pre-calculated variables from the main scope
    switch (msgtype) {
      case 'm.text':
        return processedContent.body || '[No content]';

      case 'm.image':
      case 'm.video':
      {
        const onAttachmentClick = (e) => {
          e.preventDefault();
          setIsAttachmentModalOpen(true);
          setIsHovered(false); 
        };
        const attachmentContent = (
          <div className='attachment-message'>
            <button
              type='button'
              className={'attachment-preview-btn'}
              onClick={onAttachmentClick}
              aria-label={`Download ${filename || 'file'}`}
            >
              {previewSrc && !imageLoadError ? (
                <img src={previewSrc} alt={`Preview for ${filename}`} className='attachment-preview' onError={() => setImageLoadError(true)} />
              ) : (
                <div className="attachment-preview-fallback">
                  <span>Preview not available</span>
                </div>
              )}
              {msgtype === 'm.video' && (
                <div className="video-play-overlay"><i className="fa fa-play"></i></div>
              )}
            </button>
          </div>
        );

        return attachmentContent;
      }

      case 'm.audio':
      case 'm.file': {
        const onAttachmentClick = (e) => {
          e.preventDefault();
          setIsFileModalOpen(true);
          setIsHovered(false);
        };

        const fileType = getFileType(mimeType, filename);
        const iconSrc = getAttachmentIcon(msgtype, filename, mimeType);


        return (
          <div
            className='attachment-message attachment-message--file'
            onClick={onAttachmentClick}
          >
            <div className='attachment-file-details'>
              <img src={iconSrc} alt={`Icon for ${filename}`} className={'attachment-icon'} />
              <div className="attachment-file-text-wrapper">
                <div className='attachment-filename-inline'>{filename}</div>
                <div className='attachment-file-type-inline'>{fileType}</div>
                <div className='attachment-file-size-inline'>{formattedSize}</div>
              </div>
            </div>
          </div>
        );
      }

      default:
        return processedContent.body || '[No content]';
    }
  };

  // Render message content, preserving newlines for text messages
  const messageBody = getMessageBody();
  let renderedContent = messageBody; // messageBody can be JSX or a string
  const isPlainText = typeof renderedContent === 'string';
  if (isPlainText && renderedContent.includes('\n')) {
    renderedContent = renderedContent.split('\n').map((line, idx, arr) => (
      <React.Fragment key={idx}>
        {line}
        {idx < arr.length - 1 && <br />}
      </React.Fragment>
    ));
  } else if (isPlainText && isRedacted) {
    // Wrap redacted text in a clickable span to open message details
    const handleRedactedClick = (e) => {
      e.stopPropagation();
      if (isOpeningDetails) return;
      setIsOpeningDetails(true);
      onMessageDetailsOpen(message);
      setTimeout(() => setIsOpeningDetails(false), 500);
    };
    
    renderedContent = (
      <span 
        className='deleted-message clickable' 
        onClick={handleRedactedClick}
        onMouseDown={(e) => e.preventDefault()}
        role="button"
        tabIndex={0}
        aria-label="View message deletion details"
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleRedactedClick(e);
          }
        }}
        title="Click to view message details"
      >
        {renderedContent}
      </span>
    );
  }

  // Helper function to render reply preview
  const renderReplyPreview = () => {
    if (!isReply || !originalReplyMessage) return null;

    const { content, sender, senderIsUser, isAttachment, thumbnailUrl, msgtype, filename, mimeType, size } = originalReplyMessage;
    const displaySender = senderIsUser ? 'You' : sender;

    // For attachments, append the formatted size before truncation
    let displayContent = content;
    if (isAttachment && size) {
      displayContent = `${content} (${size})`;
    }
    // Truncate displayContent if too long
    const truncatedContent = displayContent.length > 50 ? `${displayContent.substring(0, 50)}...` : displayContent;

    // Get the generic icon for non-image attachments
    const genericIconSrc = isAttachment && !thumbnailUrl
      ? getAttachmentIcon(msgtype, filename, mimeType)
      : null;

    return (
      <div className={`message-reply-preview ${isAttachment ? 'attachment' : ''}`}>
        {isAttachment && thumbnailUrl && (
          <img src={thumbnailUrl} alt="Reply thumbnail" className="message-reply-preview-thumbnail" />
        )}
        {isAttachment && genericIconSrc && (
          <img src={genericIconSrc} alt="File type icon" className="message-reply-preview-icon" />
        )}
        <div className='message-reply-preview-content'>
          <div className='message-reply-preview-sender'>{displaySender}</div>
          <div className='message-reply-preview-text'>
            {truncatedContent}
          </div>
        </div>
      </div>
    );
  };

  const renderEditIndicator = () => {
    // Don't show edit indicator for redacted messages
    if (!isEdited || isRedacted) return null;

    return (
      <span className='edit-indicator'>
        {' '}(edited)
      </span>
    );
  };

  useEffect(() => {
    if (isContextMenuOpen) {
      const handleClickOutside = (event) => {
        if (contextMenuRef.current && !contextMenuRef.current.contains(event.target)) {
          setOpenContextMenu(false);
          setIsMenuSticky(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isContextMenuOpen]);

  // Close emoji picker when clicking outside
  useEffect(() => {
    if (showEmojiPicker) {
      const handleClickOutside = (event) => {
        if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target)) {
          setShowEmojiPicker(false);
          setIsMenuSticky(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showEmojiPicker]);

  // Create or get portal container that scrolls with messages
  useEffect(() => {
    if (!portalContainerRef.current) {
      // Find the dedicated portal container in ChatView.jsx
      const portalContainer = document.getElementById('modal-portal-container');
      if (!portalContainer) {
        logger.warn('Could not find #modal-portal-container for modals.');
        return;
      }
      portalContainerRef.current = portalContainer;
    }
  }, []);

  const updateMenuPosition = useCallback((button) => {
    if (!button || !portalContainerRef.current) return;

    const buttonRect = button.getBoundingClientRect();
    const containerRect = portalContainerRef.current.getBoundingClientRect();

    // Determine menu height based on message type and permissions
    let menuHeight;
    if (isUser) {
      // Check if Edit button will be shown (only for text messages)
      const messageContent = message.getClearContent?.() || message.getContent();
      const nonEditableTypes = ['m.image', 'm.video', 'm.audio', 'm.file'];
      const isTextMessage = !nonEditableTypes.includes(messageContent?.msgtype);
      // Count items: Reply (1) + Edit (0-1) + Details (1) + Edit History (0-1) + Delete (0-1)
      // Maximum is 5 items if text message with edit history and can delete
      menuHeight = isTextMessage ? CONTEXT_MENU_HEIGHT_USER_MAX : CONTEXT_MENU_HEIGHT_USER_MIN;
    } else {
      // For other users' messages, check if we can delete (moderator)
      // Count items: Reply (1) + Details (1) + Edit History (0-1) + Delete (0-1)
      // Maximum is 4 items if message has edit history and moderator can delete
      menuHeight = canDelete ? CONTEXT_MENU_HEIGHT_OTHER_MAX : CONTEXT_MENU_HEIGHT_OTHER_MIN;
    }
    
    // Calculate position relative to the portal container (which scrolls with content)
    const relativeTop = buttonRect.top - containerRect.top;
    const relativeLeft = buttonRect.left - containerRect.left;
    
    // Check if menu would go off the bottom of the messages area
    // .messages-list has height: calc(100% - 77px), so effective bottom is viewport height - 77px
    const messagesAreaBottom = window.innerHeight - 77;
    const spaceBelow = messagesAreaBottom - buttonRect.bottom;
    const shouldExtendUpward = spaceBelow < (menuHeight + 10); // 10px buffer
    
    // Calculate horizontal position (extend left for user, right for others)
    const leftPosition = isUser 
      ? relativeLeft - CONTEXT_MENU_WIDTH 
      : relativeLeft + buttonRect.width;
    
    // Calculate vertical position (extend up if near bottom, otherwise down)
    const topPosition = shouldExtendUpward 
      ? relativeTop - menuHeight 
      : relativeTop + buttonRect.height;
    
    setMenuPosition({
      top: topPosition,
      left: leftPosition,
    });
  }, [isUser, message, canDelete]);

  const handleDeleteMessage = (messageId) => {
    showConfirm(
      t('ChatView.deleteMessageTitle'),
      t('ChatView.deleteMessageDesc'),
      () => {
        onDeleteMessage(messageId);
        setOpenContextMenu(false);
      },
      () => {
        setOpenContextMenu(false);
      },
    );
  };

  const handleMessageClick = () => {
    if (!isLastInGroup) {
      setHasRevealedDetails(!hasRevealedDetails);
    }
  };

  const handleMenuClick = (e) => {
    e.stopPropagation();
    
    const button = e.currentTarget;
    updateMenuPosition(button);
    setOpenContextMenu(true);
  };

  const renderHoverMenu = (messageId) => {
    const shouldShowMenu = isHovered || isHoveringReactions || isContextMenuOpen || isMenuSticky;

    if (!shouldShowMenu) return null;

    // Determine if this is a text message (only text messages can be edited)
    // Attachments (images, videos, files, audio) cannot be edited
    const messageContent = message.getClearContent?.() || message.getContent();
    const nonEditableTypes = ['m.image', 'm.video', 'm.audio', 'm.file'];
    const isTextMessage = !nonEditableTypes.includes(messageContent?.msgtype);

    const handleReplyClick = (e) => {
      e.stopPropagation();
      onReplyMessage(message, name, messageBody, msgtype, filename, previewSrc, fileSize);
      setOpenContextMenu(false);
      setIsHovered(false);
    };

    const handleEditClick = (e) => {
      e.stopPropagation();
      onEditMessage(message, messageBody);
      setOpenContextMenu(false);
      setIsHovered(false);
    };

    const handleDeleteClick = (e) => {
      e.stopPropagation();
      setOpenContextMenu(false);
      setIsHovered(false);
      handleDeleteMessage(messageId);
    };

    const handleMessageDetailsClick = (e) => {
      e.stopPropagation();
      onMessageDetailsOpen(message);
      setOpenContextMenu(false);
      setIsHovered(false);
    };

    const handleEditHistoryClick = (e) => {
      e.stopPropagation();
      onEditHistoryOpen(message);
      setOpenContextMenu(false);
      setIsHovered(false);
    };

    const handleReactionClick = (e) => {
      e.stopPropagation();
      setShowEmojiPicker(!showEmojiPicker);
      setIsMenuSticky(true);
    };

    const closeEmojiPicker = () => {
      setShowEmojiPicker(false);
      setIsMenuSticky(false);
    };

    // Helper to render the kebab+context or just kebab
    const renderKebabWithContext = () => (
      <>
        <div className='message-bubble-context-menu-wrapper'>
          <KebabMenuIcon onClick={handleMenuClick} />
        </div>
        {isContextMenuOpen && portalContainerRef.current && createPortal(
          <div
            ref={contextMenuRef}
            className='message-bubble-context-menu'
            style={{
              top: `${menuPosition.top}px`,
              left: `${menuPosition.left}px`,
            }}>
            {/* For other people's messages: Reply → Message Details → Edit History (if edited) → Delete (if can delete) */}
            {!isUser && (
              <>
                <div className='message-bubble-context-menu-item first' onClick={handleReplyClick}>
                  <i className='fa fa-reply' />
                  <span className='message-bubble-context-menu-item-text'>Reply</span>
                </div>
                <div
                  className='message-bubble-context-menu-item'
                  onClick={handleMessageDetailsClick}>
                  <i className='fa fa-info-circle' />
                  <span className='message-bubble-context-menu-item-text'>Message Details</span>
                </div>
                {isEdited && (
                  <div
                    className='message-bubble-context-menu-item'
                    onClick={handleEditHistoryClick}>
                    <i className='fa fa-history' />
                    <span className='message-bubble-context-menu-item-text'>Edit History</span>
                  </div>
                )}
                {canDelete && (
                  <div className='message-bubble-context-menu-item last' onClick={handleDeleteClick}>
                    <i className='fa fa-trash' />
                    <span className='message-bubble-context-menu-item-text'>
                      {t('MessageBubble.delete')}
                    </span>
                  </div>
                )}
              </>
            )}
            {/* For user's messages: Reply → Edit (if text) → Message Details → Edit History (if edited) → Delete (if can delete) */}
            {isUser && (
              <>
                <div className='message-bubble-context-menu-item first' onClick={handleReplyClick}>
                  <i className='fa fa-reply' />
                  <span className='message-bubble-context-menu-item-text'>Reply</span>
                </div>
                {isTextMessage && (
                  <div className='message-bubble-context-menu-item' onClick={handleEditClick}>
                    <i className='fa fa-edit' />
                    <span className='message-bubble-context-menu-item-text'>Edit</span>
                  </div>
                )}
                <div className='message-bubble-context-menu-item' onClick={handleMessageDetailsClick}>
                  <i className='fa fa-info-circle' />
                  <span className='message-bubble-context-menu-item-text'>Message Details</span>
                </div>
                {isEdited && (
                  <div className='message-bubble-context-menu-item' onClick={handleEditHistoryClick}>
                    <i className='fa fa-history' />
                    <span className='message-bubble-context-menu-item-text'>Edit History</span>
                  </div>
                )}
                {canDelete && (
                  <div className='message-bubble-context-menu-item last' onClick={handleDeleteClick}>
                    <i className='fa fa-trash' />
                    <span className='message-bubble-context-menu-item-text'>
                      {t('MessageBubble.delete')}
                    </span>
                  </div>
                )}
              </>
            )}
          </div>,
          portalContainerRef.current,
        )}
      </>
    );

    // Helper to render the reaction button
    const renderReactionButton = () => (
      <div className='message-bubble-reaction-button' onClick={handleReactionClick}>
        <div className='reaction-icon-container'>
          <img src='/images/svg/emoji-react.svg' alt='React' className='reaction-icon' />
        </div>
      </div>
    );

    return (
      <div
        className={`message-bubble-hover-menu ${isUserClass}`}
        onMouseEnter={handleOnHover}
        onMouseLeave={() => setIsHovered(false)} >
        {isUser ? (
          <>
            {renderKebabWithContext()}
            {renderReactionButton()}
          </>
        ) : (
          <>
            {renderReactionButton()}
            {renderKebabWithContext()}
          </>
        )}
        {showEmojiPicker && (
          <div
            ref={emojiPickerRef}
            className='message-bubble-emoji-picker-container'
            onClick={(e) => e.stopPropagation()}>
            <SmartEmojiPicker
              messageId={message.getId()}
              reactions={displayReactions}
              onToggleReaction={handleReactionToggle}
              onClose={closeEmojiPicker}
            />
          </div>
        )}
      </div>
    );
  };

  const handleReactionToggle = async (messageId, emoji, reactionEventId = null) => {
    try {
      setShowEmojiPicker(false);
      setIsMenuSticky(false);
      setIsHovered(false);

      // If unreacting, clear optimistic state
      if (reactionEventId) {
        setOptimisticReaction(null);
      } else {
        // Set optimistic reaction immediately for instant UI feedback
        setOptimisticReaction({ emoji, timestamp: Date.now() });
      }

      await toggleReaction(messageId, emoji, reactionEventId, message._reactions);
      logger.debug(
        `Toggled reaction ${emoji} on message ${messageId}`,
        reactionEventId ? 'removed' : 'added',
      );

      // Clear optimistic state after a delay to allow server update to arrive
      setTimeout(() => setOptimisticReaction(null), 1000);
    } catch (error) {
      logger.error('Failed to toggle reaction:', error);
      // Clear optimistic state on error
      setOptimisticReaction(null);
    }
  };

  const handleOnHover = () => {
    setIsHovered(true);
  };

  return (
    <div
      key={message.getId()}
      data-event-id={message.getId()}
      className={`message-item ${isEditHighlighted ? 'edit-highlighted' : ''} ${isSelected ? 'selected' : ''}`}>
      <div className='message-bubble-wrapper' onClick={handleMessageClick}>
        <div
          className={`message-bubble ${isUserClass} ${positionClass} ${
            isRedacted ? 'redacted' : ''
          } ${displayReactions && displayReactions.size > 0 ? 'has-reactions' : ''}`}
          onMouseEnter={handleOnHover}
          onMouseLeave={() => setIsHovered(false)}
          ref={bubbleRef}>
          {renderReplyPreview()}
          {renderedContent}
          {renderEditIndicator()}

          {/* Display reactions positioned at bottom right of bubble */}
          {displayReactions && displayReactions.size > 0 && (
            <MessageReactions
              reactions={displayReactions}
              onToggleReaction={handleReactionToggle}
              messageId={message.getId()}
              onHoverChange={setIsHoveringReactions}
              className={isUser ? 'user' : ''}
              onReactionDetailsOpen={onReactionDetailsOpen}
            />
          )}

          {/* Render the fullscreen attachment modal */}
          {isAttachmentModalOpen && (
            <AttachmentPreviewModal
              url={processedContent.url} // Pass the original mxc:// URL
              filename={filename}
              msgtype={msgtype}
              fileSize={fileSize}
              mimeType={mimeType}
              message={message}
              senderName={name}
              thumbnailUrl={previewSrc}
              onToggleReaction={handleReactionToggle}
              displayReactions={displayReactions}
              portalContainer={portalContainerRef.current}
              onReplyMessage={onReplyMessage}
              onFocusMessage={onFocusMessage}
              onClose={() => setIsAttachmentModalOpen(false)}
            />
          )}
          {isFileModalOpen && (
            <FileAttachmentModal
              message={message}
              senderName={name}
              thumbnailUrl={previewSrc}
              filename={filename}
              msgtype={msgtype}
              fileSize={fileSize}
              mimeType={mimeType}
              displayReactions={displayReactions}
              onReplyMessage={onReplyMessage}
              portalContainer={portalContainerRef.current}
              onFocusMessage={onFocusMessage}
              onToggleReaction={handleReactionToggle}
              onClose={() => setIsFileModalOpen(false)}
            />
          )}
        </div>
        {!isRedacted && renderHoverMenu(message.getId())}
      </div>
      {filename && !isRedacted && (
        <div className='attachment-info'>{filename} ({formattedSize})</div>
      )}


      {shouldShowDetails && (
        <div className='message-info'>
          <FancyTimeStamp
            className='message-timestamp'
            unixTime={message.getTs() / 1000}
            displayTitle={false}
          />
          {!isUser && isLastInGroup && <span className='message-sender'>{name}</span>}
          {isUser && (
            <MessageStatusIndicator
              read={isRead}
              onClick={() => onMessageDetailsOpen(message)}
            />
          )}
          {showUnverifiedIcon && <UnverifiedDeviceIcon />}
        </div>
      )}
    </div>
  );
};

MessageBubble.propTypes = {
  message: PropTypes.shape({
    isRedacted: PropTypes.func.isRequired,
    getContent: PropTypes.func.isRequired,
    getId: PropTypes.func.isRequired,
    getTs: PropTypes.func.isRequired,
    getSender: PropTypes.func,
    getRoomId: PropTypes.func,
    getType: PropTypes.func,
    getClearContent: PropTypes.func,
    decryptionError: PropTypes.shape({
      message: PropTypes.string,
      code: PropTypes.string,
    }),
    client: PropTypes.object,
    _reactions: PropTypes.instanceOf(Map),
    _isEdited: PropTypes.bool,
    _editedContent: PropTypes.object,
  }).isRequired, // It's a MatrixEvent object
  name: PropTypes.string,
  isUser: PropTypes.bool,
  shield: PropTypes.object,
  isRead: PropTypes.bool,
  isFirstInGroup: PropTypes.bool,
  isLastInGroup: PropTypes.bool,
  hasRevealedDetails: PropTypes.bool,
  onDeleteMessage: PropTypes.func,
  onReplyMessage: PropTypes.func,
  onEditMessage: PropTypes.func,
  isEditHighlighted: PropTypes.bool,
  isSelected: PropTypes.bool,
  isReply: PropTypes.bool,
  isEdited: PropTypes.bool,
  originalReplyMessage: PropTypes.shape({
    eventId: PropTypes.string,
    content: PropTypes.string,
    sender: PropTypes.string,
    senderIsUser: PropTypes.bool,
  }),
  onReactionDetailsOpen: PropTypes.func,
  onMessageDetailsOpen: PropTypes.func,
  onFocusMessage: PropTypes.func,
  onEditHistoryOpen: PropTypes.func,
};

export default MessageBubble;
